import { InputHTMLAttributes } from 'react';
import {
  SvgIcon,
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  styled
} from '@mui/material';
import { checkedStyle, checkmarkPath, indeterminatePath } from './animation';
import { getDataId } from '../shared/utils/dataId';

const CHECKBOX_SIZE = 16;
const CHECKBOX_SIZE_SM = 10.7;

function CheckedIcon({ dataId, size }: { dataId: string; size: MuiCheckboxProps['size'] }) {
  const iconSize = size === 'small' ? 10 : 16;

  return (
    <SvgIcon
      className='hd-checked-icon'
      style={{ width: iconSize, height: iconSize, fill: 'none' }}
      data-id={dataId}
    >
      <path
        d='M4.1,12.7 9,17.6 20.3,6.3'
        style={{ stroke: 'currentColor' }}
        strokeDasharray={22.910259}
        strokeLinecap='round'
        strokeWidth='2px'
        className='checkmark-path'
      />
    </SvgIcon>
  );
}

function IndeterminateIcon({ dataId, size }: { dataId: string; size: MuiCheckboxProps['size'] }) {
  const iconSize = size === 'small' ? 10 : 16;

  return (
    <SvgIcon style={{ width: iconSize, height: iconSize, fill: 'none' }} data-id={dataId}>
      <path
        d='M4 12 L 20 12'
        style={{ stroke: 'currentColor' }}
        strokeLinecap='round'
        strokeWidth='3px'
      />
    </SvgIcon>
  );
}

export const Checkbox = styled(({ dataId, ...props }: MuiCheckboxProps & { dataId: string }) => (
  <MuiCheckbox
    color='default'
    icon={<span />}
    checkedIcon={
      <CheckedIcon dataId={getDataId('checked-icon')(dataId)('checkbox')} size={props.size} />
    }
    indeterminateIcon={
      <IndeterminateIcon
        dataId={getDataId('indeterminate-icon')(dataId)('checkbox')}
        size={props.size}
      />
    }
    inputProps={
      {
        'data-id': getDataId('input')(dataId)('checkbox'),
        ...props.inputProps
      } as InputHTMLAttributes<HTMLInputElement>
    }
    disableRipple
    {...props}
    onChange={(event, checked) => {
      if (props.onChange) props.onChange(event, checked);
    }}
  />
))(() => ({
  border: 'solid 2px var(--divider-color)',
  borderRadius: 'var(--border-radius-4)',
  padding: 0,
  width: CHECKBOX_SIZE,
  height: CHECKBOX_SIZE,
  pointerEvents: 'none',
  '&.MuiCheckbox-sizeSmall': {
    width: CHECKBOX_SIZE_SM,
    height: CHECKBOX_SIZE_SM
  },
  '& .PrivateSwitchBase-input': {
    pointerEvents: 'auto'
  },
  '& .PrivateSwitchBase-input[disabled]': {
    pointerEvents: 'none !important'
  },
  '&.Mui-checked': {
    ...checkedStyle,
    '& .hd-checked-icon path': {
      '@keyframes checkmark-path': checkmarkPath,
      WebkitAnimation: '180ms linear 0s checkmark-path',
      animation: '180ms linear 0s checkmark-path'
    }
  },
  '&.MuiCheckbox-indeterminate': {
    ...checkedStyle,
    '& svg': {
      '@keyframes indeterminate-path': indeterminatePath,
      WebkitAnimation: '90ms linear 0s indeterminate-path',
      animation: '90ms linear 0s indeterminate-path'
    }
  },
  '&.Mui-disabled.Mui-checked, .Mui-disabled.MuiCheckbox-indeterminate': {
    opacity: 0.5
  },
  '&.Mui-disabled&:not(.Mui-checked):not(.MuiCheckbox-indeterminate)': {
    backgroundColor: 'var(--divider-color)'
  },
  '&:hover, &.Mui-focusVisible': {
    '&:before': {
      content: '""',
      backgroundColor: 'var(--text-default-color)',
      opacity: 0.05,
      width: '40px',
      height: '40px',
      position: 'absolute',
      borderRadius: '50%'
    }
  },
  '&.Mui-checked,&.MuiCheckbox-indeterminate': {
    '&:hover, &.Mui-focusVisible': {
      '&:before': {
        backgroundColor: 'var(--primary-color)'
      }
    }
  }
}));
